import React, { useEffect, useState } from "react";
import { commonSchedules } from "./constants";
import { formatTime, timeToSliderValue, sliderValueToTime } from "./utils";
import { useShared } from "@shared/context/SharedContext";
import { useAddWorkingHour } from "../../hooks";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { logEvent } from "@shared/analytics/analytics";


const WorkingHoursSettings: React.FC = () => {
  const { sharedState, setValue } = useShared();
  const utilityID = useRemoteUtilityId();
  const rawSelectedDay = sharedState.selectedDayObject;
  const allWorkingHours = sharedState.allWorkingHours || [];

  const selectedDay = React.useMemo(() => 
    rawSelectedDay
      ? {
          ...rawSelectedDay,
          startTime: rawSelectedDay.start_time ?? rawSelectedDay.startTime,
          endTime: rawSelectedDay.end_time ?? rawSelectedDay.endTime,
        }
      : null,
    [rawSelectedDay]
  );

  const [localStartTime, setLocalStartTime] = useState("08:00");
  const [localEndTime, setLocalEndTime] = useState("17:00");

  const { mutate: addWorkingHour } = useAddWorkingHour();

  useEffect(() => {
    if (selectedDay) {
      setLocalStartTime(selectedDay.startTime);
      setLocalEndTime(selectedDay.endTime);
    }
  }, [selectedDay]);

  const updateSlider = (type: "start" | "end", value: string) => {
    if (!selectedDay) return;

    const updatedDay = {
      ...selectedDay,
      startTime: type === "start" ? value : localStartTime,
      endTime: type === "end" ? value : localEndTime,
    };

    setValue("selectedDayObject", {
      ...updatedDay,
      start_time: updatedDay.startTime,
      end_time: updatedDay.endTime,
    });

    setLocalStartTime(updatedDay.startTime);
    setLocalEndTime(updatedDay.endTime);
  };

  const applyPreset = (preset: string) => {
    const [start, end] = preset.split(" - ");
    setLocalStartTime(start);
    setLocalEndTime(end);

    const updatedDay = {
      ...selectedDay,
      startTime: start,
      endTime: end,
    };

    setValue("selectedDayObject", {
      ...updatedDay,
      start_time: start,
      end_time: end,
    });
  };

  const handleSave = () => {
    if (!selectedDay) return;

    // Filter all active days from the shared state
    const activeDays = allWorkingHours.filter((day: any) => day.isActive);

    // If there are active days, apply the time settings to all of them.
    // Otherwise, apply only to the selected day.
    const daysToUpdate = activeDays.length > 0 ? activeDays : [selectedDay];

    const payload = {
      remote_utility_id: utilityID,
      working_hours: daysToUpdate.map((day: any) => ({
        name: day.name,
        code: day.name,
        start_time: localStartTime,
        end_time: localEndTime,
      })),
    };

    console.log("🚀 Payload to send:", payload);
    addWorkingHour(payload, {
      onSuccess: () => {
        logEvent("ONB Working Hours Updated", {
          ...payload
        })
      }
    });
  };

  if (!selectedDay) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="card-heading-text">
          Working Hours Settings
        </h3>
        <p className="text-gray-500 text-center py-8">
          Select a day to configure working hours
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <h3 className="text-lg font-medium text-gray-900 mb-4">
        Working Hours Settings
      </h3>
      <p className="text-gray-600 mb-6">
        Configure hours for{" "}
        <span className="font-semibold">{selectedDay.name}</span>
      </p>

      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Start Time{" "}
            <span className="ml-2 text-blue-600 font-medium">
              {formatTime(localStartTime)}
            </span>
          </label>
          <input
            type="range"
            min={0}
            max={1440}
            step={15}
            value={timeToSliderValue(localStartTime)}
            onChange={(e) =>
              updateSlider("start", sliderValueToTime(+e.target.value))
            }
            className="w-full h-2 bg-blue-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            End Time{" "}
            <span className="ml-2 text-blue-600 font-medium">
              {formatTime(localEndTime)}
            </span>
          </label>
          <input
            type="range"
            min={0}
            max={1440}
            step={15}
            value={timeToSliderValue(localEndTime)}
            onChange={(e) =>
              updateSlider("end", sliderValueToTime(+e.target.value))
            }
            className="w-full h-2 bg-blue-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
          />
        </div>

        <div>
          <h4 className="font-medium text-gray-900 mb-3">
            Common Work Schedules
          </h4>
          <div className="grid grid-cols-2 gap-2">
            {commonSchedules.map((schedule, index) => (
              <button
                key={index}
                onClick={() => applyPreset(schedule)}
                className="px-3 py-2 text-sm text-left border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                {schedule}
              </button>
            ))}
          </div>
        </div>

        <div className="text-right pt-4">
          <button
            onClick={handleSave}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            Save Hours
          </button>
        </div>
      </div>
    </div>
  );
};

export default WorkingHoursSettings;
